import type { ReactNode } from "react";
import { Progress, Tag, Tooltip, Typography } from "antd";
import { StarFilled, UserOutlined } from "@ant-design/icons";
import {
  LOD_LABELS,
  entriesOf,
  formatLodLabel,
  type Classifications,
  type Lod,
  type ScopeStatus
} from "design-scope";
import "./scope-item.scss";

// Single source of truth for the presentational vocabulary shared by the table,
// the detail panel, and the modal — so a status, an LOD, a classification
// system, or a "not committed yet" gap always reads the same way everywhere.

export const STATUS_META: Record<
  ScopeStatus,
  { label: string; color: string }
> = {
  NOT_STARTED: { label: "Not Started", color: "default" },
  IN_PROGRESS: { label: "In Progress", color: "processing" },
  IN_REVIEW: { label: "In Review", color: "warning" },
  COMPLETE: { label: "Complete", color: "success" }
};

export function StatusTag({ status }: { status: ScopeStatus }) {
  const meta = STATUS_META[status];
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

/** LOD as a rounded pill — the select-friendly sibling of the LodMeter bar. */
const LOD_TAG_COLOR: Record<Lod, string> = {
  1: "default",
  2: "blue",
  3: "geekblue",
  4: "purple",
  5: "green"
};

export function LodTag({ lod }: { lod: Lod }) {
  return (
    <Tag color={LOD_TAG_COLOR[lod]}>
      {lod} · {LOD_LABELS[lod]}
    </Tag>
  );
}

const SYSTEM_META: Record<string, { label: string; color: string }> = {
  UF: { label: "UF", color: "blue" },
  MF: { label: "MF", color: "geekblue" },
  CUSTOM: { label: "Custom", color: "purple" }
};

/** Small badge that tells the user which classification system a code belongs to. */
export function SystemTag({ system }: { system: string }) {
  const meta = SYSTEM_META[system] ?? { label: system, color: "default" };
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

/**
 * Combined classification badge: [ SYSTEM | code ]. The system segment
 * carries the tint; the code rides along in a lighter wash of the same hue.
 */
export function ClassBadge({
  system,
  code
}: {
  system: string;
  code?: string;
}) {
  const meta = SYSTEM_META[system] ?? { label: system, color: "default" };
  const hue =
    system === "UF"
      ? "dsm-class-uf"
      : system === "MF"
        ? "dsm-class-mf"
        : "dsm-class-custom";
  return (
    <span className={`dsm-class-badge ${hue}`}>
      <span className="dsm-class-sys">{meta.label}</span>
      {code && <span className="dsm-class-code">{code}</span>}
    </span>
  );
}

/** A CSI code (e.g. "03 30 00") rendered in the brand accent, next to its title. */
export function ClassificationLabel({
  system,
  code,
  title,
  strong
}: {
  system?: string;
  code?: string;
  title?: string;
  strong?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      {system && <SystemTag system={system} />}
      {code && (
        <Typography.Text className="text-ciq-primary-900">
          {code}
        </Typography.Text>
      )}
      <Typography.Text strong={strong}>{title}</Typography.Text>
    </span>
  );
}

/**
 * Level-of-Development shown as a 5-segment maturity meter — the fill tells the
 * user how far a scope item has developed at a glance; the tooltip spells out
 * the level for anyone who doesn't have the 1–5 scale memorised.
 */
export function LodMeter({ lod }: { lod: Lod }) {
  return (
    <Tooltip title={formatLodLabel(lod)}>
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        <Progress steps={5} percent={lod * 20} showInfo={false} />
        <Typography.Text type="secondary">L{lod}</Typography.Text>
      </span>
    </Tooltip>
  );
}

/**
 * A missing commitment/bid is a real, actionable gap in a scope matrix — flag
 * it in amber rather than hiding it behind a silent dash.
 */
export function GapTag({ label = "Unassigned" }: { label?: string }) {
  return <Tag color="warning">{label}</Tag>;
}

/** Neutral placeholder for a genuinely optional-and-empty field (e.g. milestone). */
export function NoneText({ label = "—" }: { label?: string }) {
  return <Typography.Text type="secondary">{label}</Typography.Text>;
}

export function PrimaryStar() {
  return (
    <Tooltip title="Primary Bid Package">
      <Tag color="gold">
        <StarFilled /> Primary
      </Tag>
    </Tooltip>
  );
}

/** Uppercase micro-heading above a section, with an optional right-side action. */
export function SectionLabel({
  children,
  action
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between">
      <Typography.Text
        type="secondary"
        strong
        style={{ fontSize: 11.5, letterSpacing: 0.5 }}
      >
        {String(children).toUpperCase()}
      </Typography.Text>
      {action}
    </div>
  );
}

/** Every standard an item is coded in, or a placeholder when it has none. */
export function ClassificationList({
  classification
}: {
  classification?: Classifications;
}) {
  const entries = entriesOf(classification);
  if (!entries.length)
    return (
      <Typography.Text type="secondary">No classification</Typography.Text>
    );
  return (
    <div className="flex flex-col gap-1">
      {entries.map((c) => (
        <div key={c.system} className="flex items-center gap-2">
          <ClassBadge system={c.system} code={c.code} />
          <Typography.Text className="min-w-0" ellipsis>
            {c.title || "—"}
          </Typography.Text>
        </div>
      ))}
    </div>
  );
}

/** One at-a-glance fact (label over value) for the summary grid. */
export function Glance({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div>
        <Typography.Text
          type="secondary"
          strong
          style={{ fontSize: 10.5, letterSpacing: 0.5 }}
        >
          {label.toUpperCase()}
        </Typography.Text>
      </div>
      {/* Let long values (e.g. a full primary-bid name) wrap instead of
          truncating — the glance box grows a line rather than hiding text. */}
      <div className="break-words">{children}</div>
    </div>
  );
}

/**
 * The at-a-glance summary a reader checks first: owner and primary bid.
 * Heads the detail panel.
 */
export function GlanceGrid({
  item,
  nameOfBp
}: {
  item: {
    responsibility: string;
    bids: { bidPackageId: string; primary: boolean }[];
  };
  nameOfBp: (id: string) => string;
}) {
  const primary = item.bids.find((b) => b.primary);
  return (
    <div
      className="mt-3 gap-x-4 gap-y-2 rounded-lg p-3"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        background: "var(--ciq-gray-50)",
        border: "1px solid var(--ciq-gray-200)"
      }}
    >
      <Glance label="Owner">
        <UserOutlined className="mr-1" />
        <Typography.Text>{item.responsibility || "—"}</Typography.Text>
      </Glance>
      <Glance label="Primary bid">
        {primary?.bidPackageId ? (
          <Typography.Text>
            <StarFilled
              className="mr-1"
              style={{ color: "var(--ciq-yellow-300)" }}
            />
            {nameOfBp(primary.bidPackageId)}
          </Typography.Text>
        ) : (
          <NoneText />
        )}
      </Glance>
    </div>
  );
}
